import * as React from 'react';
import { DndContext, useSensor, useSensors, PointerSensor, TouchSensor, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { darken, useTheme, Theme } from '@mui/material/styles';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import Collapse from '@mui/material/Collapse';
import ReactQuill from 'react-quill-new';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as Device from 'react-device-detect';
import { upperFirst } from 'lodash';

import 'react-quill-new/dist/quill.snow.css';
import './quill.css';

import {
  GetEventQuery,
  useUpsertEventFaqMutation,
  useRemoveEventFaqMutation,
  useSortEventFaqMutation,
  GetEventDetailedDocument,
} from '@graphql';
import { useToastError } from '@hooks';
import { WrapperContext } from '../layout';
import { FAQProps } from './Faq';

interface FAQFormProps extends FAQProps {}

export function FAQForm(props: Readonly<FAQFormProps>) {
  const context = React.useContext(WrapperContext);

  if (!context) {
    throw new Error('FAQForm must be used inside Wrapper');
  }

  return (
    <Stack spacing={2} height="100%">
      <SortableFAQ {...props} />
    </Stack>
  );
}

interface SortableFAQProps extends FAQProps {
  onChange?: (next: GetEventQuery['event']['FAQs']) => void;
}

function SortableFAQ(props: Readonly<SortableFAQProps>) {
  const { entity, entityId, FAQs: items, onChange } = props;
  const [state, setState] = React.useState(items);
  const [isAddItem, setIsAddItem] = React.useState(false);
  const [faqToUpdate, setFaqToUpdate] = React.useState<GetEventQuery['event']['FAQs'][number] | null>(null);
  const theme = useTheme();
  const toastError = useToastError();
  const { t } = useTranslation('cta');

  const disabled = state.length <= 1;

  const [sortEventFAQ] = useSortEventFaqMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetEventDetailedDocument, variables: { id: entityId } }],
    onCompleted() {
      toast.success(t('event.toast.sortFAQ.success', { ns: 'field' }));
    },
    onError(error) {
      toastError(error, t('event.toast.sortFAQ.error', { ns: 'field' }));
    },
  });

  // Keep local state in sync when parent items change
  React.useEffect(() => {
    setState([...items].sort((a, b) => a.idx - b.idx));
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const onDragEnd = async (e: DragEndEvent) => {
    if (disabled) return;
    const { active, over } = e;
    if (!over || active.id === over.id) return;

    // ✅ Reorder and reindex idx
    const prev = state; // keep a snapshot for rollback
    const oldIndex = prev.findIndex((i) => i.id === active.id);
    const newIndex = prev.findIndex((i) => i.id === over.id);

    // Reorder locally
    const next = arrayMove(prev, oldIndex, newIndex).map((it, idx) => ({ ...it, idx }));
    const prevOrder = prev.map((i) => i.id);
    const nextOrder = next.map((i) => i.id);

    // If order didn't actually change, skip network
    if (prevOrder.join('|') === nextOrder.join('|')) return;

    try {
      await sortEventFAQ({ variables: { eventId: entityId, order: nextOrder } });
      setState(next);
      onChange?.(next); // ✅ same GraphQL shape back to caller
    } catch (error) {
      // Rollback on failure
    }
  };

  if (isAddItem) {
    return <FaqForm entity={entity} entityId={entityId} isOpen={isAddItem} setIsOpen={setIsAddItem} />;
  }

  if (faqToUpdate) {
    return (
      <FaqForm
        entity={entity}
        entityId={entityId}
        isOpen={true}
        setIsOpen={() => setFaqToUpdate(null)}
        faq={faqToUpdate}
      />
    );
  }

  if (disabled) {
    return (
      <Stack spacing={2} mt={2} p={2} borderRadius={1}>
        <Button
          variant="contained"
          color="uncolored"
          startIcon={<theme.icons.add />}
          size={Device.isMobile ? 'small' : 'medium'}
          onClick={() => setIsAddItem(true)}
          sx={{ display: isAddItem ? 'none' : 'flex' }}
        >
          {t('add', { ns: 'cta' })}
        </Button>
        {state.map((item) => (
          <SortableFAQRow key={item.id} item={item} entity={entity} entityId={entityId} disableDrag />
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={2} mt={2}>
      <Button
        variant="contained"
        color="uncolored"
        startIcon={<theme.icons.add />}
        size={Device.isMobile ? 'small' : 'medium'}
        onClick={() => setIsAddItem(true)}
        sx={{ display: isAddItem ? 'none' : 'flex' }}
      >
        {t('add', { ns: 'cta' })}
      </Button>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <SortableContext items={state.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          {state.map((item) => (
            <SortableFAQRow key={item.id} item={item} setFaqToUpdate={setFaqToUpdate} {...props} />
          ))}
        </SortableContext>
      </DndContext>
    </Stack>
  );
}

interface SortableFAQRowProps {
  entity: FAQProps['entity'];
  entityId: FAQProps['entityId'];
  item: FAQProps['FAQs'][number];
  disableDrag?: boolean;
  setFaqToUpdate?: (faq: FAQProps['FAQs'][number] | null) => void;
}

// Single row component with drag handle (GraphQL shape: { id, idx, question, answer })
function SortableFAQRow(props: SortableFAQRowProps) {
  const { entity, entityId, item, disableDrag, setFaqToUpdate } = props;
  const [isPreviewing, setIsPreviewing] = React.useState(false);
  const [isConfirmDelete, setIsConfirmDelete] = React.useState(false);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  const theme = useTheme();
  const toastError = useToastError();
  const { t } = useTranslation('cta');

  const iconSize = { xs: '1rem', sm: '1.2rem' };

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [removeFAQ, { loading: removing }] = useRemoveEventFaqMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetEventDetailedDocument, variables: { id: entityId } }],
    variables: {
      id: item.id,
    },
    onCompleted: ({ removeEventFAQ }) => {
      toast.success(t('event.toast.removeFAQ.success', { ns: 'field' }));
      setIsConfirmDelete(false);
    },
    onError(error) {
      toastError(error, t('event.toast.removeFAQ.error', { ns: 'field' }));
      setIsConfirmDelete(false);
    },
  });

  const handleUpdate = () => {
    setFaqToUpdate && setFaqToUpdate(item);
  };

  return (
    <Paper
      ref={setNodeRef}
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1.5,
        border: '1px solid rgba(255,255,255,0.1)',
        touchAction: 'none',
        userSelect: 'none',
        backgroundColor: darken(theme.palette.background.paper, 0.05),
      }}
      style={style}
    >
      <Stack spacing={1}>
        <Typography variant="body2" fontWeight="bold" fontSize={{ xs: '0.9rem', sm: '1rem' }}>
          {item.question}
        </Typography>

        {/* If `answer` is HTML, swap the block below for a sanitized `dangerouslySetInnerHTML` */}
        <Collapse in={isPreviewing} timeout="auto" unmountOnExit>
          <Typography
            variant="body2"
            sx={{ mt: 0.5 }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.answer) }}
          />
        </Collapse>
        <Divider />

        {isConfirmDelete ? (
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} m={0} p={0}>
            <Button variant="outlined" size="small" color="error" loading={removing} onClick={removeFAQ}>
              {t('confirm')}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="uncolored"
              onClick={() => setIsConfirmDelete((v) => !v)}
              disabled={removing}
            >
              {t('cancel')}
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} m={0} p={0}>
            {/* Drag handle: keep it simple and mobile-friendly */}
            {!disableDrag && (
              <IconButton
                aria-label="move"
                {...attributes}
                {...listeners}
                sx={{ cursor: 'grab', touchAction: 'none' }}
              >
                <theme.icons.move sx={{ fontSize: iconSize }} />
              </IconButton>
            )}
            <IconButton
              aria-label="preview"
              aria-expanded={isPreviewing}
              onClick={() => setIsPreviewing((v) => !v)}
            >
              {isPreviewing ? (
                <theme.icons.expandLess sx={{ fontSize: iconSize }} />
              ) : (
                <theme.icons.expandMore sx={{ fontSize: iconSize }} />
              )}
            </IconButton>
            <IconButton aria-label="edit" onClick={handleUpdate}>
              <theme.icons.edit sx={{ fontSize: iconSize }} />
            </IconButton>
            <IconButton
              aria-label="delete"
              aria-expanded={isPreviewing}
              onClick={() => setIsConfirmDelete((v) => !v)}
            >
              <theme.icons.delete sx={{ fontSize: iconSize }} />
            </IconButton>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

interface FaqFormProps {
  isOpen: boolean;
  entity: FAQProps['entity'];
  entityId: FAQProps['entityId'];
  setIsOpen: (isOpen: boolean) => void;
  faq?: FAQProps['FAQs'][number];
}

const FaqForm = (props: FaqFormProps) => {
  const { entityId, entity, isOpen, setIsOpen, faq } = props;
  const { t } = useTranslation('field');
  const toastKey = faq ? 'updateFAQ' : 'addFAQ';

  const [upsertFAQ, { loading }] = useUpsertEventFaqMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetEventDetailedDocument, variables: { id: entityId } }],
    onCompleted: ({ upsertEventFAQ }) => {
      toast.success(t(`event.toast.${toastKey}.success`, { ns: 'field' }));
      handleClose();
    },
    onError(error) {
      toast.error(t(`event.toast.${toastKey}.error`, { ns: 'field' }));
      handleClose();
    },
  });

  const formik = useFormik({
    initialValues: {
      question: faq?.question || '',
      answer: faq?.answer || '<p><br></p>',
    },
    validationSchema: faqValidationSchema(t),
    onSubmit: (values) => {
      console.log(`[values]:`, values);
      upsertFAQ({
        variables: {
          input: {
            eventId: entityId,
            id: faq?.id,
            question: values.question,
            answer: values.answer,
          },
        },
      });
    },
  });

  const handleClose = () => {
    setIsOpen(false);
    formik.resetForm();
  };

  const handleSave = () => {
    formik.handleSubmit();
  };

  React.useEffect(() => {
    // seek for the h3 button
    const h3Button = document.querySelector('.ql-picker-item[data-value="3"]');

    // Verify if the element exists before modifying it
    if (h3Button) {
      h3Button.setAttribute('data-label', 'Titre'); // Modifier l'étiquette
      h3Button.innerHTML = '';
    }
  }, []);

  return (
    isOpen && (
      <form onSubmit={formik.handleSubmit} style={{ height: '100%' }}>
        <Stack
          spacing={2}
          height="100%"
          justifyContent="space-between"
          sx={{
            border: '1px solid rgba(255,255,255,0.1)',
            p: 2,
            backgroundColor: (theme: Theme) => darken(theme.palette.background.paper, 0.05),
            borderRadius: (theme: Theme) => theme.borderRadius,
          }}
        >
          <Stack spacing={2} height="100%" justifyContent="space-between">
            <TextField
              fullWidth
              id="question"
              name="question"
              label={upperFirst(t('word.question', { ns: 'common' }))}
              value={formik.values.question}
              onChange={formik.handleChange}
              error={formik.touched.question && Boolean(formik.errors.question)}
              helperText={formik.touched.question && formik.errors.question}
            />
            <ReactQuill
              theme="snow"
              value={formik.values.answer}
              onChange={(content) => formik.setFieldValue('answer', content)}
              placeholder={upperFirst(t('word.answer', { ns: 'common' }))}
            />
          </Stack>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} m={0} p={0}>
            <Button size="small" variant="contained" color="uncolored" onClick={handleClose}>
              {t('cancel', { ns: 'cta' })}
            </Button>
            <Button
              size="small"
              variant="contained"
              type="submit"
              color="success"
              disabled={loading || !formik.isValid || !formik.dirty}
              loading={loading}
              onClick={handleSave}
            >
              {t('save', { ns: 'cta' })}
            </Button>
          </Stack>
        </Stack>
      </form>
    )
  );
};

function faqValidationSchema(t: TFunction) {
  return Yup.object({
    question: Yup.string()
      .required()
      .label(upperFirst(t('word.question', { ns: 'common' })))
      .min(3),
    answer: Yup.string()
      .required()
      .label(upperFirst(t('word.answer', { ns: 'common' })))
      .min(11)
      .test(
        'is-not-empty',
        upperFirst(t('word.answer', { ns: 'common' })),
        (value) => value !== '<p><br></p>'
      ),
  });
}
